goog.provide('yext.m4.confcode.adminconsole.AdminConsoleController');

goog.require('yext.m4.codeeditor.CodeEditor');
goog.require('yext.m4.confcode.adminconsole.BusinessSelectorModal');
goog.require('yext.m4.confcode.adminconsole.ConfirmationModalController');
goog.require('yext.m4.confcode.adminconsole.ConsoleService');
goog.require('yext.m4.confcode.adminconsole.Repositories');
goog.require('yext.m4.confcode.adminconsole.ShowDiffModal');
goog.require('yext.m4.confcode.adminconsole.Sidebar');
goog.require('yext.m4.confcode.adminconsole.ToolsPanelComponent');
goog.require('yext.m4.confcode.adminconsole.VariablesModal');
goog.require('yext.m4.confcode.adminconsole.MissingResourcesModal');
goog.require('yext.m4.confcode.adminconsole.modal.AddResourceModal');
goog.require('yext.m4.confcode.adminconsole.modal.DeleteModal');
goog.require('yext.m4.confcode.adminconsole.modal.HelpModal');
goog.require('yext.m4.confcode.adminconsole.modal.SettingsModal');
goog.require('yext.m4.confcode.adminconsole.routes');
goog.require('yext.m4.confcode.adminconsole.shapes');
goog.require('yext.m4.errors.DiagnosticData');
goog.require('yext.m4.errors.ErrorsService');
goog.require('yext.m4.confcode.adminconsole.resources.ResourceNodeManager');

/**
 * @typedef {{
 *   name: !string,
 *   children: !Array.<ResourcesJsonTree>,
 *   relativePath: string
 * }}
 */
var ResourcesJsonTree;

/**
 * @typedef {!Object}
 */
var Content;

/**
 * @enum {!string}
 */
var _BusinessSelectorMode = {
  APPLY: 'apply',
  DIFF: 'diff',
};

const _resourceTypeToFallbackNamespace = new Map(Object.entries({
  'km/entity-type': 'ce',
  'km/field': 'c',
  'km/field-type': 'c',
  'km/field-presentation': 'ce',
  'analytics/insight': 'c',
}));

/**
 * @typedef {{
 *   name: string,
 *   value: string,
 * }}
 */
let Variable;

/**
 * @typedef {{
 *   shouldClose: boolean,
 *   errorMessage: string,
 * }}
 */
let VariablesSubmitResponse;

/**
 * @typedef {Map<string, VariableType>}
 */
let VariablesMetadata;

/**
 * @typedef {{
 *   displayName: string,
 *   toolTip: string,
 *   type: string,
 * }}
 */
let VariableType;

/**
 * Controller for the Admin Console.
 *
 * @author Benjamin Leitner (bleitner@yext.com)
 */
yext.m4.confcode.adminconsole.AdminConsoleController = class {
  constructor() {
    this._$adminConsoleContainer = $('.js-adminconsole-container');

    const {ResourceNodeManager} =
      goog.module.get('yext.m4.confcode.adminconsole.resources.ResourceNodeManager');
    const {ResourceNode} = goog.module.get('yext.m4.confcode.adminconsole.shapes');

    this._resourceNodeManager =
      new ResourceNodeManager(
        /** @type {Array<ResourceNode>} */ (this._$adminConsoleContainer.data('resource-nodes')),
        /** @type {Object} */ (this._$adminConsoleContainer.data('namespaced-relative-path-to-content-map')));
    this._sourceBusinessId = this._getSourceBusinessId();
    this._shouldEnableComprehensiveApply = this._$adminConsoleContainer.data('should-enable-comprehensive-apply');

    this._resourceGroupToTypes =
      this._$adminConsoleContainer.data('resource-groups-to-types');

    this._variablesMetadata = this._parseVariablesMetadata();

    this._organizeByResourceType =
      /** @type {boolean} */
      (this._$adminConsoleContainer.data('organize-by-resource-type'));
    this._shouldShowFriendlyNames =
      /** @type {boolean} */
      (this._$adminConsoleContainer.data('should-show-friendly-names'));
    this._shouldIncludeReadOnly =
      /** @type {boolean} */
      (this._$adminConsoleContainer.data('should-include-readonly'));

    this._hasHighResourceCount =
      /** @type {boolean} */
      (this._$adminConsoleContainer.data('has-high-resource-count'));

    this._caller =
      /** @type {string} */
      (this._$adminConsoleContainer.data('caller'));

    this._treeSelectMode = 'none';
    this._sidebarCurrentBranchId = 'root';
    this._variablesByBusinessId = {};
    this._closedVariableIdsByBusinessId = {};

    // Initalize components
    this._$consoleContainer = this._$adminConsoleContainer.find('.js-console');
    this._$showDiffModalContainer = this._$adminConsoleContainer.find('.js-show-diff-modal-container');
    this._errorsService = this._initializeErrorService();
    this._codeEditor = this._initializeCodeEditor();
    this._console = this._initializeConsole();
    this._$variablesModalContainer = this._$adminConsoleContainer.find('.js-variables-modal-container');
    this._$missingResourcesModalContainer = this._$adminConsoleContainer.find('.js-missing-resources-modal-container');
    this._confirmationModal = this._initializeConfirmationModalController();
    this._toolsPanel = /** @type {Element} */ (this._$adminConsoleContainer.find('.js-tools-panel').get(0));
    this._openFileForDiagnostic = this._openFileForDiagnostic.bind(this);
    this._$settingsDialogContainer = this._$adminConsoleContainer.find('.js-settings-dialog-container');
    this._$helpModalContainer = this._$adminConsoleContainer.find('.js-help-modal-container');
    this._addFile = this._addFile.bind(this);
    this._showAddResourceModal = this._showAddResourceModal.bind(this);
    this._showDeleteSelectedResourceFilesModal = this._showDeleteSelectedResourceFilesModal.bind(this);
    this._showDeleteDirectoryModal = this._showDeleteDirectoryModal.bind(this);
    this._showDeleteFileModal = this._showDeleteFileModal.bind(this);
    this._showHelpDialogModal = this._showHelpDialogModal.bind(this);
    this._variablesFormSubmit = this._variablesFormSubmit.bind(this);

    // Bind event listeners
    this._bindApply();
    this._bindShowDiff();
    this._bindButtonAddResource();
    this._bindButtonBulkSelectResources();
    this._bindButtonBulkDeleteResources();
    this._bindButtonSettings();
    this._bindButtonHelp();
    this._bindButtonDownload();

    this._resourceInEditor = this._loadPathResourceFromUrlHash();
    this._printConsoleWelcome();
    this._registerOverviewDiagnostics();
    this._initializeVariables();

    this._renderSidebar();
  }

  /**
   * Initializes the error state class with the necessary callbacks and timeouts.
   * @private
   * @return {!yext.m4.errors.ErrorsService}
   */
  _initializeErrorService() {
    return new yext.m4.errors.ErrorsService(
      errorsState => {
        this._codeEditor.setCodeDiagnostics(errorsState['errors'], errorsState['warnings']);
        errorsState['errors']
          .filter(d => !!d.fileLocation)
          .forEach(node => this._resourceNodeManager.setErrorsForNode(node));
        this._console.appendErrors(errorsState['errors']);
        this._console.appendWarnings(errorsState['warnings']);
        this._renderToolsPanel();
        this._renderSidebar();
      },
      removedErrorsState => {
        this._codeEditor.removeCodeDiagnostics(
          removedErrorsState['removedErrors'], removedErrorsState['removedWarnings']);
        removedErrorsState['errors']
          .filter(d => !!d.fileLocation)
          .forEach(node => this._resourceNodeManager.clearErrorsForNode(node));
        this._renderSidebar();
      });
  }

  /**
   * @private
   * Initializes the code editor.
   *
   * @return {!yext.m4.codeeditor.CodeEditor} The code editor
   */
  _initializeCodeEditor() {
    const container =
      /** @type {!HTMLElement} */
      (this._$adminConsoleContainer.find('.js-codeeditor-container').get(0));
    return new yext.m4.codeeditor.CodeEditor(
      container,
      'public/js/monaco-workers/',
      this._errorsService,
      'adminconsole' /* editorSettingsUriPrefix */);
  }

  /**
   * @private
   * Initializes the console.
   *
   * @return {!yext.m4.confcode.adminconsole.ConsoleService} The console
   */
  _initializeConsole() {
    return new yext.m4.confcode.adminconsole.ConsoleService(
      this._renderToolsPanel.bind(this));
  }

  /**
   * @private
   * Renders the sidebar.
   */
  _renderSidebar() {
    const onExpandDirectory = nodeId => {
      const node = this._resourceNodeManager.getResourceById(nodeId, this._organizeByResourceType);
      if (node['expanded']) {
        this._resourceNodeManager.collapseNodeAndChildren(this._organizeByResourceType, nodeId);
      } else {
        this._resourceNodeManager.expandNodeAndParents(this._organizeByResourceType, nodeId);
      }
      this._renderSidebar();
    };

    const onSelectFile = (_, id, readOnly) => {
      this._navigateToFile(id);
      this._codeEditor.updateReadOnly(readOnly);
      this._renderSidebar();
    };

    const resourceNodes = Array.from(
      this._organizeByResourceType
        ? this._resourceNodeManager.getResourcesByResourceTypePath().values()
        : this._resourceNodeManager.getResourcesByNamespacePath().values());


    const onBulkSelect = (nodeId, shouldBeBulk) => {
      this._resourceNodeManager.updateBulkSelected(this._organizeByResourceType, nodeId, shouldBeBulk);
      this._renderSidebar();
    };

    const props = {
      resourceNodes: resourceNodes,
      rootNodeId: this._sidebarCurrentBranchId,
      onAddOrUploadFiles: this._showAddResourceModal,
      onExpandDirectory: onExpandDirectory,
      onSelectFile: onSelectFile,
      selectedNodeId: this._resourceInEditor,
      shouldOrganizeByResourceType: this._organizeByResourceType,
      shouldShowFriendlyNames: this._shouldShowFriendlyNames,
      shouldIncludeReadOnly: this._shouldIncludeReadOnly,
      treeSelectMode: this._treeSelectMode,
      onDeleteFile: this._showDeleteFileModal,
      onDeleteDirectory: this._showDeleteDirectoryModal,
      onDoubleClickDirectory: nodeId => this._sidebarCurrentBranchId = nodeId,
      onBulkSelect: onBulkSelect,
    };

    const {Sidebar} = goog.module.get('yext.m4.confcode.adminconsole.Sidebar');
    const domElement =
      /** @type {Element} */
      (this._$adminConsoleContainer.find('.js-sidebar').get(0));

    ReactDOM.render(
      React.createElement(Sidebar, props, null),
      domElement);
  }

  /**
   * @private
   *
   * Shows the delete modal for the specified directory.
   *
   * @param {string} nodeId the directory node id
   */
  _showDeleteDirectoryModal(nodeId) {
    const DeleteModal = goog.module.get('yext.m4.confcode.adminconsole.modal.DeleteModal');

    const onDelete = () => {
      if (this._organizeByResourceType) {
        this._resourceNodeManager.deleteResourceTypeDirectory(nodeId);
      } else {
        this._resourceNodeManager.deleteNamespacedDirectory(nodeId);
      }
      this._hideDeleteResourceDialog();
      this._renderSidebar();
    };

    const props = {
      fileName: nodeId,
      showDialog: true,
      onDelete: onDelete,
    };

    const domElement =
      /** @type {Element} */
      (this._$adminConsoleContainer.find('.js-delete-modal-container').get(0));

    ReactDOM.render(
      React.createElement(DeleteModal, props, null),
      domElement,
    );
  }

  /**
   * @private
   *
   * Adds a file with the specified params.
   *
   * @param {string} namespace the namespace to which the file belongs to
   * @param {string} resourceType the resource type, e.g., km/entity
   * @param {string} fileName the resource file name
   */
  _addFile(namespace, resourceType, fileName) {
    const adjustedNamespace =
      namespace === ''
        ? this._getFallbackNamespace(resourceType)
        : namespace;
    this._resourceNodeManager.addFile(
      adjustedNamespace, resourceType, fileName);

    const addedPath = this._organizeByResourceType
      ? resourceType + '/' + fileName
      : adjustedNamespace + '/' + resourceType + '/' + fileName;

    this._codeEditor.setTemplateValue(resourceType, adjustedNamespace, addedPath, () => {});
    this._navigateToFile(addedPath);

    this._renderSidebar();
  }

  /**
   * @private
   * @param {string} resourceType
   * @returns the fallback namespace
   */
  _getFallbackNamespace(resourceType) {
    return _resourceTypeToFallbackNamespace.has(resourceType)
      ? _resourceTypeToFallbackNamespace.get(resourceType)
      : 'default';
  }

  /**
   * @private
   *
   * Navigates to the file specified by the given path.
   *
   * @param {string} path the path
   */
  _navigateToFile(path) {
    if (this._resourceInEditor) {
      const oldSelectedFile =
        this._resourceNodeManager.getResourceById(
          this._resourceInEditor, this._organizeByResourceType);

      if (oldSelectedFile) {
        this._resourceNodeManager.updateResourceContentForNamespacedPath(
          oldSelectedFile['namespacedPath'], this._codeEditor.getValue());
      }
    }

    window.location.hash = path;
    this._resourceInEditor = path;
    this._resourceNodeManager.expandNodeAndParents(
      this._organizeByResourceType, path);


    const content = this._resourceNodeManager.getResourceContent(
      this._organizeByResourceType, path);
    if (content) {
      const resource = this._resourceNodeManager.getResourceById(path, this._organizeByResourceType);
      const namespace =
        resource['namespacedParentId'] === 'root'
          ? ''
          : resource['namespacedPath'].split('/')[0];
      this._codeEditor.setValue(content, namespace, path, () => {});
    }
  }

  /**
   * @private
   * Initializes the variables.
   */
  _initializeVariables() {
    const initialVariables = /** @type {!Object} */ (this._$adminConsoleContainer.data('variables'));
    if (this._sourceBusinessId && initialVariables) {
      this._variablesByBusinessId[this._sourceBusinessId] = initialVariables;
      this._updateClosedVariables(this._sourceBusinessId);
    }
  }

  /**
   * @private
   * Initializes the confirmation modal controller.
   *
   * @return {!yext.m4.confcode.adminconsole.ConfirmationModalController} The confirmation modal controller
   */
  _initializeConfirmationModalController() {
    return new yext.m4.confcode.adminconsole.ConfirmationModalController(
      this._$adminConsoleContainer.find('.js-confirmation-modal-container'),
      this._getEnvironment(),
    );
  }

  /**
   * @private
   * Loads the resource specified in the hash of the URL if present. To do so, this function does
   * the following:
   *   1. Sets the value of the code editor to the resource contents of the relative path
   *  2. Returns the linked path if there is one
   * @return the linked path
   */
  _loadPathResourceFromUrlHash() {
    // Remove '#' character from the URL hash
    const linkedPath = window.location.hash.substring(1);

    if (this._resourceNodeManager.getResourceContent(
      this._organizeByResourceType, linkedPath)) {
      this._navigateToFile(linkedPath);
      return linkedPath;
    }

    return null;
  }

  /**
   * Logs the errors and warnings that happened in overview.
   * @private
   */
  _registerOverviewDiagnostics() {
    /**
     * @type {!Array}
     */
    const errorDiagnosticsData =
      /** @type {Array} */ (this._$adminConsoleContainer.data('error-diagnostics')) || [];

    /**
     * @type {!Array}
     */
    const warningDiagnosticsData =
      /** @type {Array} */ (this._$adminConsoleContainer.data('warning-diagnostics')) || [];

    if (errorDiagnosticsData.length || warningDiagnosticsData.length) {
      this._console.appendText(
        'There were errors or warnings while pulling resources. Please see the errors / warnings tab for details.');
    }
    this._registerDiagnostics(errorDiagnosticsData, warningDiagnosticsData);
  }

  /**
   * Calls the errors service with the given diagnostics.
   *
   * @param {!Array<!string>} errorsDiagnosticsData   - Unparsed error diagnostics
   * @param {!Array<!string>} warningDiagnosticsData  - Unparsed warning diagnostics
   * @private
   */
  _registerDiagnostics(errorsDiagnosticsData, warningDiagnosticsData) {
    const warningDiagnostics = [];
    const errorDiagnostics = [];
    for (const warning of warningDiagnosticsData) {
      const parsedJSON = /** @type {!Object} */ (JSON.parse(warning));
      warningDiagnostics.push(yext.m4.errors.DiagnosticData.fromJSONDiagnostic(parsedJSON));
    }
    for (const error of errorsDiagnosticsData) {
      const parsedJSON = /** @type {!Object} */ (JSON.parse(error));
      errorDiagnostics.push(yext.m4.errors.DiagnosticData.fromJSONDiagnostic(parsedJSON));
    }
    this._errorsService.setDiagnostics(errorDiagnostics, warningDiagnostics);
  }

  /**
   * Parses variables metadata into Json Object
   *
   * @return {!VariablesMetadata} Parsed Json Object
   * @private
   */
  _parseVariablesMetadata() {
    const varsObj = /** @type {!Object} */ (this._$adminConsoleContainer.data('variables-metadata'));
    if (varsObj) {
      try {
        let varsNameToType = new Map();
        for (let k of Object.keys(varsObj)) {
          varsNameToType.set(k, varsObj[k]);
        }
        return /** @type {!VariablesMetadata} */ (varsNameToType);
      } catch (e) {
        this._console.appendText('An error occurred while parsing vars.json ' + e);
        return new Map();
      }
    }
    return new Map();
  }

  /**
   * Updates the closed variables tracker.
   *
   * If the closed variables are not set, it sets them to the current variable set.
   * If the closed variables are set, it retains the ones that have not been removed.
   *
   * @param {number} businessId - The ID of the business the variables belong to
   */
  _updateClosedVariables(businessId) {
    if (businessId in this._closedVariableIdsByBusinessId) {
      // make sure we don't retain any values that were deleted by the user
      const currentVariables = Object.keys(this._variablesByBusinessId[businessId]);
      this._closedVariableIdsByBusinessId[businessId] =
        this._closedVariableIdsByBusinessId[businessId].filter(v => currentVariables.includes(v));
    } else {
      this._closedVariableIdsByBusinessId[businessId] =
        Object.keys(this._variablesByBusinessId[businessId]);
    }
  }

  /**
   * Gets the variables as an array
   *
   * @param {number} businessId - The ID of the business whose variables should be returned
   * @return {!Array<!Variable>} The variables array, which is an array of objects with two
   *                           properties: 'name' and 'value'. 'name' represents the name of
   *                           the variable and 'value' represent the value of the variable.
   *                           The following is an example of a variables array:
   *                           [
   *                             {
   *                               'name': 'city',
   *                               'value': 'Philadelphia'
   *                             },
   *                             {
   *                               'name': 'region',
   *                               'value': 'PA'
   *                             }
   *                           ]
   */
  _getVariablesAsArray(businessId) {
    const variablesArray = [];
    const variablesMap = this._variablesByBusinessId[businessId];
    if (variablesMap) {
      for (const key in variablesMap) {
        const variable = {};
        variable['name'] = key;
        variable['value'] = variablesMap[key];
        variablesArray.push(variable);
      }
    }
    return variablesArray;
  }

  /**
   * @private
   * Gets the business ID resources were pulled from.
   *
   * @return {?number}
   */
  _getSourceBusinessId() {
    const businessId = this._$adminConsoleContainer.data('source-business-id');
    return businessId
      ? Number(businessId)
      : null;
  }

  /**
   * @private
   * Gets the namespace used when pulling resources.
   *
   * @return {?string}
   */
  _getSourceNamespace() {
    const namespace = this._$adminConsoleContainer.data('source-namespace');
    return namespace
      ? String(namespace)
      : null;
  }

  /**
   * @private
   * Gets the name of business that resources were pulled from
   *
   * @return {?string}
   */
  _getSourceBusinessName() {
    const businessName = this._$adminConsoleContainer.data('source-business-name');
    return businessName
      ? String(businessName)
      : null;
  }

  /**
   * @private
   * Gets the environment that's currently being used: dev, qa, sbx, prod
   * @return {!string}
   */
  _getEnvironment() {
    return String(this._$adminConsoleContainer.data('environment'));
  }

  /**
   * @private
   * Binds event listeners for the variable handling and then apply with confirmation.
   */
  _bindApply() {
    // Handler for when the "Apply" button is clicked
    this._$adminConsoleContainer.find('.js-apply').on('click', () => {
      this._resourceNodeManager.updateResourceContent(
        this._organizeByResourceType,
        this._resourceInEditor,
        this._codeEditor.getValue(),
      );
      this._renderBusinessSelectorModal(
        true,
        _BusinessSelectorMode.APPLY,
        (businessId, businessName, missingDependencyBehavior, namespace, isComprehensive, isDry) => {
          this._showMissingResourcesModal(() => {
            this._openVariablesModal(businessId, () => {
              this._diff(businessId, namespace, isComprehensive, () => {
                this._confirmationModal.open(
                  businessId,
                  businessName,
                  this._hasDependencyFile(),
                  missingDependencyBehavior,
                  isDry,
                  isComprehensive);
              });

              // Clear all previous events for "confirmApply"
              $(this._confirmationModal).off('confirmApply');
              // Handler for when the apply action is confirmed
              $(this._confirmationModal).on('confirmApply', () => {
                this._apply(businessId, missingDependencyBehavior, namespace, isDry, isComprehensive);
              });
            });
          });
        });
    });
  }

  /**
   * Updates the internal variables mapping by removing variables that no longer exist and
   * adding newly created variables with empty string as the initial value.
   * Displays the variables modal based on the following conditions:
   *    - If no variables to set, go to next action
   *    - Otherwise, display the modal
   *
   * @param {number} businessId
   * @param {function()} nextAction
   * @private
   */
  _openVariablesModal(businessId, nextAction) {
    const relativePathToContent = this._resourceNodeManager.getNamespacedPathToContentMap();
    const anyVariableNameRegex = RegExp(/\$\{\{.+?\}\}/, 'g');
    const validVariableNameRegex = RegExp(/\$\{\{[\w\/.-]+?\}\}/, 'g');
    const updatedVariables = {};
    const invalidVariableNames = [];
    relativePathToContent.forEach(val => {
      let arr;
      while ((arr = anyVariableNameRegex.exec(val)) !== null) {
        const variable = arr[0];
        // Remove "${{" and "}}" from the variable name
        const variableName = variable.substring(3, arr[0].length - 2);
        if (!variable.match(validVariableNameRegex)) {
          invalidVariableNames.push(variableName);
        }
        const variablesMap = this._variablesByBusinessId[businessId] || {};
        if (variableName === 'businessId') {
          updatedVariables[variableName] = businessId;
        } else if (variablesMap[variableName]) {
          updatedVariables[variableName] = variablesMap[variableName];
        } else {
          updatedVariables[variableName] = ''; // Empty string variable values are not supported
        }
      }
    });
    this._variablesByBusinessId[businessId] = updatedVariables;
    this._updateClosedVariables(businessId);
    if (Object.keys(updatedVariables).length > 0) {
      this._showVariablesModal(businessId, invalidVariableNames, nextAction);
    } else {
      nextAction();
    }
  }

  /**
   * @private
   * Displays the variables modal.
   *
   * @param {number} businessId - The ID of the business the modal is showing variables for
   * @param {!Array<string>} invalidVariableNames - The invalid variable names
   * @param {function()} nextAction - Next action to take after the modal closes
   */
  _showVariablesModal(businessId, invalidVariableNames, nextAction) {
    const props = {
      variablesMetadata: this._variablesMetadata,
      validVariables: this._getVariablesAsArray(businessId),
      businessId: businessId,
      invalidVariableNames: invalidVariableNames,
      submit: this._variablesFormSubmit,
      nextAction: nextAction,
    };

    const {VariablesModal} = goog.module.get('yext.m4.confcode.adminconsole.VariablesModal');
    const domElement = /** @type {Element} */ (this._$variablesModalContainer.get(0));

    ReactDOM.render(
      React.createElement(VariablesModal, props),
      domElement);
  }

  /**
   * @private
   * Saves the variables set from the modal view. If all variables are assigned a value, the
   * internal variables mapping is updated and the modal is closed. Otherwise, renders an error
   * message and doesn't close the modal.
   *
   * @param {number} businessId - ID of business the variables should be saved for
   * @param {!Array<!Variable>} variables - Variables set from the modal view
   * @return {!VariablesSubmitResponse} Whether the modal should close, and error message if not
   */
  _variablesFormSubmit(businessId, variables) {
    let areAllVariablesSet = true;
    const invalidValueKeys = [];
    // Iterate through variable values to check whether all variables are assigned a value
    variables.forEach(variable => {
      const name = variable['name'];
      const value = variable['value'];

      if (value === '' && !this._closedVariableIdsByBusinessId[businessId].includes(name)) {
        invalidValueKeys.push(name);
        areAllVariablesSet = false;
      }
    });

    if (areAllVariablesSet) {
      const variablesMap = {};
      variables.forEach(variable => {
        // Save the variables mapping
        variablesMap[variable['name']] = variable['value'];
        // Validate variables
        if (this._variablesMetadata && this._variablesMetadata.has(variable['name'])) {
          this._validateVariable(this._variablesMetadata.get(variable['name']), variable['value']);
        }
      });

      this._variablesByBusinessId[businessId] = variablesMap;
      return {shouldClose: true, errorMessage: ''};
    }

    return {
      shouldClose: false,
      errorMessage: 'All user introduced variables must have a value assigned. Fix ' +
        invalidValueKeys.join(', ')};
  }

  /**
   * Validates variable against variable metadata.
   *
   * @param {!VariableType} variable - The variable type to be validated against
   * @param {string} input - The input to be validated
   * @private
   */
  _validateVariable(variable, input) {
    $.ajax({
      method: 'POST',
      url: yext.m4.confcode.adminconsole.routes.AdminConsole.validateVariable({}),
      data: {
        'variable': variable,
        'input': input,
      },
    }).done(response => {
      if (!response['isValid']) {
        alert(response['message']);
      }
    }).fail(e => {
      if (e.statusText !== 'abort') {
        alert(`Variable validation server responded with error ${e.statusText}`);
      }
    });
  }

  /**
   * @private
   * Displays the missing resources modal if it has high resource count.
   *
   * @param {function()} nextAction - Next action to take after the modal closes
   */
  _showMissingResourcesModal(nextAction) {
    if (!this._hasHighResourceCount) {
      nextAction();
      return;
    }

    const props = {
      nextAction: nextAction,
    };

    const {MissingResourcesModal} =
      goog.module.get('yext.m4.confcode.adminconsole.MissingResourcesModal');
    const domElement =
      /** @type {Element} */ (this._$missingResourcesModalContainer.get(0));

    ReactDOM.render(
      React.createElement(MissingResourcesModal, props),
      domElement);
  }

  /**
   * @private
   * Binds event listeners for the show diff workflow.
   */
  _bindShowDiff() {
    this._$adminConsoleContainer.find('.js-show-diff').on('click', () => {
      this._updateOpenFile();
      this._renderBusinessSelectorModal(
        false,
        _BusinessSelectorMode.DIFF,
        (businessId, businessName, missingDependencyBehavior, namespace, isComprehensive) => {
          this._showMissingResourcesModal(() => {
            this._openVariablesModal(
              businessId,
              () => this._diff(businessId, namespace, isComprehensive, null));
          });
        });
    });
  }
  /**
   * @private
   * Binds event listener for when a resource is added.
   */
  _bindButtonAddResource() {
    this._$adminConsoleContainer.find('.js-add-resource').on('click', () => {
      this._showAddResourceModal('root');
    });
  }

  /**
   * @private
   * Binds event listener for when the help modal is clicked.
   */
  _bindButtonHelp() {
    this._$adminConsoleContainer.find('.js-help').on('click', () => {
      this._showHelpDialogModal();
    });
  }

  /**
   * @private
   *
   * Renders the add resource modal with the correct inputs based on the provided node id.
   * The node id represent the values which should be prefilled, i.e. if nodeId is km/entity, then the
   * resource modal will pre-load for a km/entity resource.
   *
   * @param {string} nodeId the node id
   */
  _showAddResourceModal(nodeId) {
    const pathComponents = nodeId.split('/');
    const topLevelPathComponent = pathComponents[0];

    let modifiedNodeId = nodeId;
    if (!this._resourceGroupToTypes[topLevelPathComponent]) {
      // Update the relative path of the resource by removing the namespace
      if (pathComponents.length === 1) {
        modifiedNodeId = '';
      } else {
        modifiedNodeId = nodeId.substring(nodeId.indexOf('/') + 1);
      }
    }
    const namespace =
      this._resourceGroupToTypes[topLevelPathComponent]
        && nodeId !== 'root' && !this._organizeByResourceType
          ? topLevelPathComponent
          : '';

    const relativePaths = this._organizeByResourceType
      ? this._resourceNodeManager.getResourcesByResourceTypePath().values()
      : this._resourceNodeManager.getResourcesByNamespacePath().values();

    const props = {
      selectedNode: modifiedNodeId,
      relativePaths: Array.from(relativePaths),
      namespace: namespace,
      resourceTypeMapping: this._resourceGroupToTypes,
      openModal: true,
      onCreate: this._addFile,
      showResourceTypePath: this._organizeByResourceType,
    };

    const domElement =
      /** @type {Element} */
      (this._$adminConsoleContainer.find('.js-add-resource-modal-container').get(0));
    const AddResourceModal =
      goog.module.get('yext.m4.confcode.adminconsole.modal.AddResourceModal');
    ReactDOM.render(
      React.createElement(AddResourceModal, props, null),
      domElement);

    // TODO (bleitner): Figure out how to use the boostrap modal method directly, instead of using a button
    $(domElement).find('.r-add-resource-modal-toggle').click();
  }

  /**
   * @private
   * Renders the delete file modal for the provided resource node.
   *
   * @param {*} resourceNode
   */
  _showDeleteFileModal(resourceNode) {
    const DeleteModal = goog.module.get('yext.m4.confcode.adminconsole.modal.DeleteModal');

    const onDelete = () => {
      if (this._organizeByResourceType) {
        this._resourceNodeManager.deleteFileByResourceTypeId(resourceNode['id']);
      } else {
        this._resourceNodeManager.deleteFileByNamespacedId(resourceNode['id']);
      }

      if (this._resourceInEditor === resourceNode['id']) {
        this._codeEditor.setValueSimple('', '');
      }

      this._hideDeleteResourceDialog();
      this._renderSidebar();
    };
    const props = {
      fileName: resourceNode['id'],
      showDialog: resourceNode['displayName'],
      onDelete: onDelete,
    };

    const domElement =
      /** @type {Element} */
      (this._$adminConsoleContainer.find('.js-delete-modal-container').get(0));

    ReactDOM.render(
      React.createElement(DeleteModal, props, null),
      domElement);
  }

  /**
   * @private
   * Binds event listener to enter mode to bulk select resources.
   */
  _bindButtonBulkSelectResources() {
    const $bulkSelectElement = this._$adminConsoleContainer.find('.js-bulk-select');
    $bulkSelectElement.on('click', () => {
      $bulkSelectElement.toggleClass('active');
      this._treeSelectMode === 'none' ? this._treeSelectMode = 'bulk' : this._treeSelectMode = 'none';

      // Handle enabling and disabling the bulk action icons
      const $bulkDeleteElement = this._$adminConsoleContainer.find('.js-bulk-delete');
      if (this._treeSelectMode === 'bulk') {
        $bulkDeleteElement.removeClass('disabled');
      } else {
        $bulkDeleteElement.addClass('disabled');
      }

      this._renderSidebar();
    });
  }

  /**
   * @private
   * Binds event listener to bulk delete resources.
   */
  _bindButtonBulkDeleteResources() {
    const $bulkDeleteElement = this._$adminConsoleContainer.find('.js-bulk-delete');
    $bulkDeleteElement.on('click', () => {
      if (!$bulkDeleteElement.hasClass('disabled')) {
        this._showDeleteSelectedResourceFilesModal();
      }
    });
  }

  /**
   * @private
   * Renders the delete modal for the bulk selected files.
   */
  _showDeleteSelectedResourceFilesModal() {
    let resourcesToDelete =
      this._resourceNodeManager.getBulkSelected(this._organizeByResourceType);

    // if nothing is bulk selected, don't render modal
    if (resourcesToDelete.directoriesSelected.length === 0 && resourcesToDelete.filesSelected.length === 0) {
      return;
    }

    const onDelete = () => {
      this._resourceNodeManager.deleteBulkSelected(this._organizeByResourceType, resourcesToDelete);
      this._hideDeleteResourceDialog();
      this._renderSidebar();
    };

    const DeleteModal = goog.module.get('yext.m4.confcode.adminconsole.modal.DeleteModal');
    const props = {
      resourcesToDelete: resourcesToDelete,
      showDialog: true,
      onDelete: onDelete,
    };

    const domElement =
      /** @type {Element} */
      (this._$adminConsoleContainer.find('.js-delete-modal-container').get(0));
    ReactDOM.render(
      React.createElement(DeleteModal, props, null),
      domElement);
  }

  /**
   * @private
   * Binds event listener for when the settings button is clicked.
   */
  _bindButtonSettings() {
    const $settingsButtonElement = this._$adminConsoleContainer.find('.js-settings');
    $settingsButtonElement.on('click', () => {
      const SettingsModal = goog.module.get('yext.m4.confcode.adminconsole.modal.SettingsModal');
      const props = {
        showDialog: true,
        isInFriendlyNameMode: this._shouldShowFriendlyNames,
        shouldShowReadOnlyResources: this._shouldIncludeReadOnly,
        isInResourceTypeMode: this._organizeByResourceType,
        onSave: (isInFriendlyNameMode, shouldShowReadOnlyResources, isInResourceTypeMode) =>
          this._saveSettings(isInFriendlyNameMode, shouldShowReadOnlyResources, isInResourceTypeMode),
      };
      const element = /** @type {Element} */ (this._$settingsDialogContainer.get(0));

      ReactDOM.render(
        React.createElement(SettingsModal, props, null),
        element);
    });
  }

  /**
   * @private
   * shows the help modal
  */
  _showHelpDialogModal() {
    const {HelpModal} = goog.module.get('yext.m4.confcode.adminconsole.modal.HelpModal');
    const props = {
      showDialog: true,
    };
    const domElement =
      /** @type {Element} */
      (this._$helpModalContainer.get(0));
    ReactDOM.render(
      React.createElement(HelpModal, props),
      domElement);
  }

  /**
   * @private
   * hides the delete resource modal
   */
  _hideDeleteResourceDialog() {
    const DeleteModal = goog.module.get('yext.m4.confcode.adminconsole.modal.DeleteModal');
    const props = {
      showDialog: false,
    };

    const domElement =
      /** @type {Element} */
      (this._$adminConsoleContainer.find('.js-delete-modal-container').get(0));
    ReactDOM.render(
      React.createElement(DeleteModal, props, null),
      domElement);
  }

  /**
   * @private
   * Saves the admin console settings.
   *
   * @param {!boolean} isInFriendlyNameMode
   * @param {!boolean} shouldShowReadOnlyResources
   * @param {!boolean} isInResourceTypeMode
   */
  _saveSettings(isInFriendlyNameMode, shouldShowReadOnlyResources, isInResourceTypeMode) {
    const oldShouldShowFriendlyNames = this._shouldShowFriendlyNames;
    this._shouldShowFriendlyNames = isInFriendlyNameMode;

    const oldShouldIncludeReadOnly = this._shouldIncludeReadOnly;
    this._shouldIncludeReadOnly = shouldShowReadOnlyResources;

    const oldOrganizeByResourceType = this._organizeByResourceType;
    this._organizeByResourceType = isInResourceTypeMode;

    // If the organize by resource type flag has changed, we need to update the hash value of the
    // resource in the editor to reflect the new path.
    if (oldOrganizeByResourceType !== isInResourceTypeMode) {
      // If there is a hashed file, update the hashed value
      if (this._resourceInEditor) {
        const resourceInEditor = this._resourceNodeManager.getResourceById(this._resourceInEditor, oldOrganizeByResourceType);
        this._resourceInEditor = isInResourceTypeMode
          ? resourceInEditor['resourceTypeId']
          : resourceInEditor['namespacedId'];
        window.location.hash = this._resourceInEditor;
      }
    }

    // If any of the flags has changed, we need to update the URL query param
    // The history API is only supported on newer browsers, so we need to confirm that this
    // function exists before calling it
    if (oldShouldShowFriendlyNames !== isInFriendlyNameMode
      || oldShouldIncludeReadOnly !== shouldShowReadOnlyResources
      || oldOrganizeByResourceType !== isInResourceTypeMode) {
      if (window.history) {
        let searchParams = new URLSearchParams(window.location.search);
        searchParams.set('showFriendlyNames', isInFriendlyNameMode.toString());
        searchParams.set('includeReadOnly', shouldShowReadOnlyResources.toString());
        searchParams.set('organizeByResourceType', isInResourceTypeMode.toString());
        const newurl =
          window.location.protocol +
          '//' +
          window.location.host +
          window.location.pathname +
          '?' +
          searchParams.toString() +
          window.location.hash;
        window.history.pushState({path: newurl}, '', newurl);
      }
    }

    this._renderSidebar();
  }

  /**
   * @private
   * Binds event listener for when the download button is clicked.
   */
  _bindButtonDownload() {
    const $downloadButtonElement = this._$adminConsoleContainer.find('.js-download');
    $downloadButtonElement.on('click', () => {
      this._showMissingResourcesModal(() => {
        this._downloadResources();
      });
    });
  }

  /**
   * @private
   * Downloads the resources in the current console.
   */
  _downloadResources() {
    const resourceFiles = this._generateResourceFilesArray();
    const downloadData = {
      'resourceFiles': resourceFiles,
    };

    let xhr = new XMLHttpRequest();
    if (this._sourceBusinessId) {
      xhr.open(
        'POST',
        yext.m4.confcode.adminconsole.routes.AdminConsole.downloadResourcesFromBusiness(
          {'businessId': this._sourceBusinessId}));
    } else {
      xhr.open('POST',
        yext.m4.confcode.adminconsole.routes.AdminConsole.downloadResources(
          {}));
    }

    // Note that src/com/yext/bulk/js/common/common.js was used a base for the state change
    // handling
    xhr.onreadystatechange = () => {
      // Check that the request is done
      if (xhr.readyState == 4) {
        if (xhr.status == 200) {
          const blob = xhr['response'];
          const filename = 'resources.zip';

          // Special handling for IE
          if (window.navigator && window.navigator.msSaveOrOpenBlob) {
            window.navigator.msSaveOrOpenBlob(blob, filename);
          } else {
            // In all other browsers: generate a URL, make a hidden a tag that links to the
            // generated URL, click the a tag, and free up memory for the generated URL
            const url = window.URL.createObjectURL(blob);
            let $a = document.createElement('a');
            $a['href'] = url;
            $a['download'] = filename;
            $a['style'] = 'display: none';

            document.body.appendChild($a);
            $a.click();
            $a.remove();
            window.URL.revokeObjectURL(url);
          }
        } else if (xhr.status == 403) {
          this._console.appendText(
            'Your authorization session has expired. Please reauthorize for the business you ' +
            'are downloading resources from and try again.');
        } else if (xhr.status == 500) {
          this._console.appendText(
            'An error occurred while downloading resources. Please try again or contact your ' +
            'administrator if this issue persists');
        }
      }
    };

    xhr.responseType = 'blob';
    xhr.setRequestHeader('Content-type',
      'application/x-www-form-urlencoded; charset=UTF-8');
    xhr.setRequestHeader('X-Requested-With', 'XMLHttpRequest');
    xhr.send(JSON.stringify(downloadData));
  }

  /**
   * Opens the file associated with the diagnostic
   *
   * @param {!yext.m4.errors.DiagnosticData.Diagnostic} diagnostic
   * @private
   */
  _openFileForDiagnostic(diagnostic) {
    if (diagnostic.fileLocation) {
      this._updateOpenFile();


      const selectedRelativePath = diagnostic.fileLocation.filePath;
      this._navigateToFile(selectedRelativePath);
      const node = this._resourceNodeManager.getResourceById(this._resourceInEditor, this._organizeByResourceType);
      const namespace = node['namespacedPath'].split('/')[0];
      this._codeEditor.setValue(
        this._resourceNodeManager.getResourceContent(this._organizeByResourceType, selectedRelativePath), namespace, selectedRelativePath, () => {});
      this._codeEditor.jumpToDiagnostic(selectedRelativePath, diagnostic);
      this._renderSidebar();
    }
  }


  /**
   * @private
   *
   * Updates the contents of the file open in the editor.
   */
  _updateOpenFile() {
    if (this._resourceInEditor) {
      this._resourceNodeManager.updateResourceContent(
        this._organizeByResourceType,
        this._resourceInEditor,
        this._codeEditor.getValue(),
      );
    }
  }

  /**
   * @private
   */
  _renderToolsPanel() {
    const _ToolsPanelComponent = goog.module.get('yext.m4.confcode.adminconsole.ToolsPanelComponent');

    ReactDOM.render(
      React.createElement(
        _ToolsPanelComponent,
        {
          diagnosticInfo: {
            errors: this._errorsService.getCurrentDiagnostics()['errors'],
            warnings: this._errorsService.getCurrentDiagnostics()['warnings'],
            onSelectDiagnostic: this._openFileForDiagnostic,
          },
          consoleLines: this._console.getConsoleOutput(),
        },
        null),
      this._toolsPanel);
  }

  /**
   * @private
   *
   * @param {!boolean} useDependencyFile
   * @param {!_BusinessSelectorMode} mode
   * @param {!function(!number, !string, !string, ?string, !boolean, !boolean)} onSelect
   */
  _renderBusinessSelectorModal(useDependencyFile, mode, onSelect) {
    const {BusinessSelectorModal} =
      goog.module.get('yext.m4.confcode.adminconsole.BusinessSelectorModal');
    const Repositories =
      goog.module.get('yext.m4.confcode.adminconsole.Repositories');
    const element =
      /** @type {Element} */
      (this._$adminConsoleContainer.find('.js-business-selector-modal-container').get(0));

    ReactDOM.render(
      React.createElement(
        BusinessSelectorModal,
        {
          currentBusinessId: this._getSourceBusinessId(),
          sourceNamespace: this._getSourceNamespace(),
          onSelect: onSelect,
          shouldHandleMissingDependencies:
            useDependencyFile
            && this._hasDependencyFile(),
          allowAddingDependencies: this._getEnvironment().toLowerCase() !== 'prod',
          businessSelectorFor: mode,
          checkBusinessAccess: Repositories.checkBusinessAccess,
          completeSession: Repositories.completeSession,
          shouldEnableComprehensiveApply: this._shouldEnableComprehensiveApply,
        },
        null),
      element);
  }

  /**
   * @private
   * Makes an apply resources request.
   */
  _apply(businessId, missingDependencyBehavior, namespace, isDry, isComprehensive) {
    const resourceFiles = this._generateResourceFilesArray();
    if (isDry) {
      this._console.appendText('Executing dry apply...');
    } else {
      this._console.appendText('Applying resources...');
    }

    let applyData = {
      'resourceFiles': resourceFiles,
      'variables': this._getVariablesAsArray(businessId),
      'missingDependencyBehavior': missingDependencyBehavior,
      'isDry': isDry,
      'isComprehensive': isComprehensive,
      'caller': this._caller,
    };
    if (namespace != null) {
      applyData.namespace = namespace;
    }
    $.ajax({
      method: 'POST',
      url: yext.m4.confcode.adminconsole.routes.AdminConsole.apply({'businessId': businessId}),
      data: applyData,
    }).done(response => {
      const actions = response['actions'];

      if (actions.length > 0) {
        if (isDry) {
          this._console.appendText('If these resources are persisted, the following actions will be taken:');
        }

        this._console.appendTextArray(actions);
      }

      this._registerDiagnostics([], response['warningDiagnostics'] || []);

      if (response['warningDiagnostics'].length) {
        this._console.appendText(
          'There were warnings during apply. Please see the errors / warnings tab for details.');
      }

      if (isDry) {
        this._console.appendText('Dry apply finished.');
      } else {
        this._console.appendText('Successfully applied resources.');
      }
    }).fail(response => {
      // Retrieve the response JSON object from the "response" object which is type jqXHR.
      const responseJson = response['responseJSON'];
      const actions = responseJson['actions'];
      if (actions) {
        this._console.appendTextArray(actions);
      }

      this._registerDiagnostics(
        responseJson['errorDiagnostics'] || [],
        responseJson['warningDiagnostics'] || []);

      this._console.appendText(
        'An error occurred while applying resources. Please see the errors / warnings tab for details.');
    });
  }

  /**
   * @private
   * Makes a diff request.
   *
   * @param {!number} businessId
   * @param {?string} namespace
   * @param {!boolean} isComprehensive
   * @param {null|function()} continueAction -  The function that's called after the continue button
   *                                            is clicked. Continue will not be rendered if this
   *                                            function is null.
   */
  _diff(businessId, namespace, isComprehensive, continueAction) {
    const loadingProps = {
      isLoading: true,
      continueAction: continueAction,
      enableContinue: false,
    };
    this._renderShowDiffModal(loadingProps);

    let diffData = {
      'resourceFiles': this._generateResourceFilesArray(),
      'variables': this._getVariablesAsArray(businessId),
      'isComprehensive': isComprehensive,
    };
    if (namespace != null) {
      diffData.namespace = namespace;
    }

    $.ajax({
      method: 'POST',
      url: yext.m4.confcode.adminconsole.routes.AdminConsole.diff({'businessId': businessId}),
      data: diffData,
    }).done(response => {
      const props = {
        resourceDiffs: response,
        continueAction: continueAction,
      };
      this._renderShowDiffModal(props);
    }).fail(response => {
      const errors = response['responseJSON']['errorDiagnostics'];
      const errorDiagnostics = [];
      for (const error of errors) {
        const parsedJSON = /** @type {!Object} */ (JSON.parse(error));
        errorDiagnostics.push(yext.m4.errors.DiagnosticData.fromJSONDiagnostic(parsedJSON));
      }

      const props = {
        errors: errorDiagnostics,
        continueAction: continueAction,
      };
      this._renderShowDiffModal(props);
    });
  }

  /**
   * @private
   * Renders the React show diff modal component.
   */
  _renderShowDiffModal(props) {
    const ShowDiffModal = goog.module.get('yext.m4.confcode.adminconsole.ShowDiffModal');
    const element = /** @type {Element} */ (this._$showDiffModalContainer.get(0));

    ReactDOM.render(
      React.createElement(ShowDiffModal, props, null),
      element);

    // Only toggle the modal if the diff is loading
    if (props.isLoading) {
      // TODO (bleitner): Figure out how to use the boostrap modal method directly, instead of using a button
      $(this._$showDiffModalContainer).find('.r-show-diff-modal-toggle').click();
    }
  }

  /**
   * @private
   * Generates a formatted resource files array to include in POST requests.
   * @return {!Object}
   */
  _generateResourceFilesArray() {
    let resourceFiles = [];
    this._resourceNodeManager.getNamespacedPathToContentMap()
      .forEach((content, path) => {
        const resourceFile = {
          'relativePath': path,
          'content': content,
        };
        resourceFiles.push(resourceFile);
      });
    return resourceFiles;
  }

  /**
   * @private
   * Returns true, if dependency file is present.
   * @returns {boolean}
   */
  _hasDependencyFile() {
    return this._resourceNodeManager.getNamespacedPathToContentMap()
      .has('dependencies.json');
  }

  /**
   * @private
   */
  _printConsoleWelcome() {
    const businessName = this._getSourceBusinessName();
    if (businessName) {
      this._console.appendText('Account: ' + businessName);
    }
  }
};
